import { Onboarding } from "sdk/onboardings";
import { MetaByPath } from "../config";
import { NextStep, NextStepProps } from "./constants";

interface ComputeStep extends NextStepProps {
  isAuthenticated: boolean;
  questionnaireOrder: string[];
  metaByPath: MetaByPath;
}

export const computeStep = ({
  attemptingRoute,
  isAuthenticated,
  onboarding,
  questionnaireOrder,
  metaByPath,
}: ComputeStep): NextStep => {
  const lastIndex = questionnaireOrder.length;
  const noopRoutePayload = {
    route: questionnaireOrder[1],
    isExternal: false,
  };
  const completedRoutePayload = {
    route: questionnaireOrder[questionnaireOrder.length - 1],
    isExternal: false,
  };
  const attemptingRouteIndex = questionnaireOrder.indexOf(attemptingRoute);
  const attemptingRouteConfig = metaByPath[attemptingRoute];
  const attemptingRoutePayload = {
    route: attemptingRoute,
    isExternal: false,
  };
  const isVisitor = !isAuthenticated || !onboarding;
  const isPageProtected = !attemptingRouteConfig.isPublic;

  if (isVisitor && isPageProtected) {
    return noopRoutePayload;
  }

  if (
    isVisitor &&
    attemptingRouteConfig.isPublic &&
    attemptingRouteConfig.isOptional
  ) {
    return attemptingRoutePayload;
  }

  const {
    nextUnansweredPayload,
    nextUnansweredIndex,
    nextRequiredPayload,
    nextRequiredIndex,
  } = computeNextSteps({ onboarding, metaByPath, questionnaireOrder });

  if (!nextUnansweredPayload || !nextUnansweredIndex) {
    if (attemptingRouteConfig) return attemptingRoutePayload;

    return completedRoutePayload;
  }

  if (!attemptingRouteConfig.canRevisit(onboarding)) {
    return (
      (nextUnansweredPayload?.route && nextUnansweredPayload) ||
      (nextRequiredPayload?.route && nextRequiredPayload) || {
        route: questionnaireOrder[lastIndex - 2] as string,
        isExternal: false,
      }
    );
  }

  const isSkippingRequiredQuestion =
    nextRequiredIndex &&
    attemptingRouteIndex > nextRequiredIndex &&
    nextRequiredPayload?.route;

  if (isSkippingRequiredQuestion) {
    return nextRequiredPayload;
  } else {
    return attemptingRoutePayload;
  }
};

interface ComputeNextSteps {
  onboarding: Partial<Onboarding>;
  metaByPath: MetaByPath;
  questionnaireOrder: string[];
}

const computeNextSteps = ({
  onboarding,
  metaByPath,
  questionnaireOrder,
}: ComputeNextSteps) => {
  const lastIndex = questionnaireOrder.length - 1;
  let route = "";
  let index;
  let nextRequired;

  for (let i = questionnaireOrder.length - 1; i >= 0; i--) {
    const nextRouteConfig = metaByPath[questionnaireOrder[i]];
    const isAnswered = nextRouteConfig.isAnswered(onboarding);

    if (isAnswered) {
      nextRequired = findNextRequired({
        onboarding,
        metaByPath,
        questionnaireOrder,
      });
      route = questionnaireOrder[i + 1] || questionnaireOrder[lastIndex - 1];
      index = i + 1;
      break;
    }
  }

  return {
    nextUnansweredPayload: {
      route,
      isExternal: false,
    },
    nextUnansweredIndex: index,
    nextRequiredPayload: nextRequired?.payload,
    nextRequiredIndex: nextRequired?.index,
  };
};

interface FindNextRequiredProps {
  onboarding: Partial<Onboarding>;
  metaByPath: MetaByPath;
  questionnaireOrder: string[];
}

const findNextRequired = ({
  onboarding,
  metaByPath,
  questionnaireOrder,
}: FindNextRequiredProps) => {
  const lastIndex = questionnaireOrder.length - 1;
  let route;
  let index;

  for (let j = 0; j < questionnaireOrder.length; j++) {
    const possibleNextRouteConfig = metaByPath[questionnaireOrder[j]];
    const isNotOptional = !possibleNextRouteConfig.isOptional;
    const isPreviousAnswered = possibleNextRouteConfig.isAnswered(onboarding);
    if (isNotOptional && !isPreviousAnswered) {
      route = questionnaireOrder[j];
      index = j;
      break;
    }
  }
  return {
    payload: {
      route: route ? route : questionnaireOrder[lastIndex - 1],
      isExternal: false,
    },
    index,
  };
};
